import Check from 'lucide-react-native/icons/check';
import ChevronDown from 'lucide-react-native/icons/chevron-down';
import type { LucideIcon } from 'lucide-react-native';
import { useRef, useState } from 'react';
import {
  Dimensions,
  Modal,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

export type DropdownOption = { value: string; label: string; icon?: LucideIcon };

type Anchor = { x: number; y: number; width: number; height: number };

const MENU_MIN_WIDTH = 200;

export function Dropdown({
  options,
  value,
  onChange,
  icon,
  label,
  style,
  labelStyle,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  /** Overrides the selected option's icon in the trigger. */
  icon?: LucideIcon;
  /** Overrides the selected option's label in the trigger. */
  label?: string;
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const triggerRef = useRef<View>(null);

  const selected = options.find((o) => o.value === value);
  const TriggerIcon = icon ?? selected?.icon;
  const triggerText = label ?? selected?.label ?? '';

  const openMenu = () => {
    const node = triggerRef.current;
    if (!node) {
      setOpen(true);
      return;
    }
    node.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setOpen(true);
    });
  };

  const menuLayout = () => {
    if (!anchor) return null;
    const screen = Dimensions.get('window');
    const width = Math.max(anchor.width, MENU_MIN_WIDTH);
    const left = Math.min(Math.max(anchor.x, 8), screen.width - width - 8);
    const top = anchor.y + anchor.height + 4;
    return { width, left, top };
  };

  const layout = menuLayout();

  return (
    <>
      <Pressable
        ref={triggerRef}
        accessibilityRole="button"
        accessibilityLabel={triggerText}
        onPress={openMenu}
        style={({ pressed }) => [style, pressed && { backgroundColor: theme.secondary }]}>
        {TriggerIcon ? <TriggerIcon size={16} color={theme.gold} /> : null}
        <Text numberOfLines={1} style={labelStyle}>
          {triggerText}
        </Text>
        <ChevronDown size={14} color={theme.mutedForeground} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          {layout ? (
            <View
              style={[
                styles.menu,
                {
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                  top: layout.top,
                  left: layout.left,
                  width: layout.width,
                },
              ]}>
              <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
                {options.map((opt) => {
                  const OptIcon = opt.icon;
                  const active = opt.value === value;
                  return (
                    <Pressable
                      key={opt.value}
                      accessibilityRole="button"
                      onPress={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      style={({ pressed }) => [
                        styles.option,
                        active && { backgroundColor: theme.accent },
                        pressed && !active && { backgroundColor: theme.secondary },
                      ]}>
                      {OptIcon ? (
                        <OptIcon
                          size={16}
                          color={active ? theme.accentForeground : theme.gold}
                        />
                      ) : (
                        <View style={styles.iconSpacer} />
                      )}
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.optionText,
                          {
                            color: active ? theme.accentForeground : theme.text,
                            fontWeight: active ? '600' : '500',
                          },
                        ]}>
                        {opt.label}
                      </Text>
                      {active ? <Check size={15} color={theme.accentForeground} /> : null}
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
  },
  menu: {
    position: 'absolute',
    borderWidth: 1,
    borderRadius: Radius.md,
    paddingVertical: 4,
    overflow: 'hidden',
  },
  scroll: {
    maxHeight: 300,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
    paddingHorizontal: Spacing.two + 2,
    paddingVertical: Spacing.two,
  },
  iconSpacer: {
    width: 16,
  },
  optionText: {
    flex: 1,
    fontSize: 14,
  },
});
